// Verify a company ADMIN's password — used to approve sensitive POS actions
// (e.g. deleting a bill) without disturbing the current (cashier) login session.
//
// Rules (per requirement):
//  - If the logged-in user IS an admin/owner → they re-type their OWN password
//    (re-authenticate the current user; no session change).
//  - If a non-admin (cashier) is logged in → they must enter the company
//    OWNER's password, verified via a throwaway secondary Firebase app so the
//    cashier stays logged in.

import { initializeApp, deleteApp } from 'firebase/app';
import {
  getAuth, signInWithEmailAndPassword, signOut,
  EmailAuthProvider, reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, firebaseConfig } from '../config/firebase';

const isAdminUser = (companyUser, activeCompany, currentUser) =>
  companyUser?.role === 'admin' ||
  (!!activeCompany?.ownerId && !!currentUser?.uid && activeCompany.ownerId === currentUser.uid);

export const verifyAdminPassword = async ({ companyId, currentUser, companyUser, activeCompany, password }) => {
  if (!password) return false;

  // Case 1: an admin/owner is operating → verify THEIR OWN password.
  if (isAdminUser(companyUser, activeCompany, currentUser) && auth.currentUser?.email) {
    try {
      await reauthenticateWithCredential(
        auth.currentUser,
        EmailAuthProvider.credential(auth.currentUser.email, password)
      );
      return true;
    } catch {
      return false;
    }
  }

  // Case 2: a non-admin (cashier) → verify the company OWNER's password.
  const ownerId = activeCompany?.ownerId;
  if (!ownerId || !companyId) return false;
  let ownerEmail = null;
  try {
    const snap = await getDoc(doc(db, 'companies', companyId, 'users', ownerId));
    ownerEmail = snap.exists() ? (snap.data().email || null) : null;
  } catch {
    ownerEmail = null;
  }
  if (!ownerEmail) return false;

  let secondary = null;
  try {
    secondary = initializeApp(firebaseConfig, `pwverify-${Date.now()}`);
    const secAuth = getAuth(secondary);
    await signInWithEmailAndPassword(secAuth, ownerEmail, password);
    await signOut(secAuth);
    return true;
  } catch {
    return false;
  } finally {
    if (secondary) { try { await deleteApp(secondary); } catch { /* ignore */ } }
  }
};

export default verifyAdminPassword;
